import { useMemo } from 'react';
import { useAuthStore } from '../store/authStore';
import { useDirectFileTransferStore } from '../store/directFileTransferStore';
import { directFileTransferManager } from '../services/directFileTransfer';
import { useI18n } from '../i18n';
import { saveBlobWithNativePicker } from '../utils/webviewBridge';

export function DirectFileTransferOverlay() {
  const { t } = useI18n();
  const incomingRequest = useDirectFileTransferStore((state) => state.incomingRequest);
  const waitingRequest = useDirectFileTransferStore((state) => state.waitingRequest);
  const activeSession = useDirectFileTransferStore((state) => state.activeSession);
  const identities = useAuthStore((state) => state.identities);

  const currentServerUrl = useMemo(() => {
    if (!activeSession && !incomingRequest && !waitingRequest) return '';
    const currentDeviceId = useAuthStore.getState().currentDeviceId;
    const device = currentDeviceId ? useAuthStore.getState().devices[currentDeviceId] : null;
    if (!device) return '';
    return identities[device.identityId]?.serverUrl || '';
  }, [activeSession, incomingRequest, waitingRequest, identities]);

  const currentIdentityId = useMemo(() => {
    if (!activeSession && !incomingRequest && !waitingRequest) return '';
    const currentDeviceId = useAuthStore.getState().currentDeviceId;
    const device = currentDeviceId ? useAuthStore.getState().devices[currentDeviceId] : null;
    return device?.identityId || '';
  }, [activeSession, incomingRequest, waitingRequest]);

  const statusText = useMemo(() => {
    if (!activeSession) return '';
    if (activeSession.status === 'awaiting_accept') return t('directFileTransfer.waitingForAcceptance');
    if (activeSession.status === 'cancelled') return t('directFileTransfer.cancelled');
    if (activeSession.status === 'rejected') return t('directFileTransfer.rejected');
    if (activeSession.status === 'failed') return t('directFileTransfer.failed');
    if (activeSession.status === 'completed') return t('directFileTransfer.completed');
    return `${activeSession.progress}%`;
  }, [activeSession, t]);

  const isTerminalState = activeSession
    ? activeSession.status === 'completed' || activeSession.status === 'cancelled' || activeSession.status === 'rejected' || activeSession.status === 'failed'
    : false;

  if (!incomingRequest && !activeSession && !waitingRequest) return null;

  return (
    <div className="fixed inset-x-4 bottom-4 z-[70] flex justify-center">
      <div className="w-full max-w-md rounded-[1.4rem] border border-[color:var(--border-soft)] bg-white p-4 shadow-[0_20px_48px_rgba(53,38,27,0.18)]">
        {incomingRequest ? (
          <div className="space-y-3">
            <div>
              <div className="text-sm font-semibold text-[color:var(--text-strong)]">{t('directFileTransfer.incomingTitle')}</div>
              <div className="mt-1 text-sm text-[color:var(--text-muted)]">
                {incomingRequest.fromIdentityName || incomingRequest.fromIdentityId}
              </div>
            </div>
            <div className="rounded-[1rem] border border-[color:var(--border-soft)] bg-[color:var(--surface-soft)] p-3 text-sm">
              <div className="font-medium text-[color:var(--text-strong)]">{incomingRequest.metadata.fileName}</div>
              <div className="mt-1 text-[color:var(--text-muted)]">{Math.ceil(incomingRequest.metadata.size / 1024)} KB</div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="btn btn-secondary flex-1"
                onClick={() => directFileTransferManager.rejectIncomingTransfer(incomingRequest.sessionId)}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary flex-1"
                onClick={() => {
                  void directFileTransferManager.acceptIncomingTransfer(
                    incomingRequest.sessionId,
                    currentServerUrl,
                    currentIdentityId
                  );
                }}
              >
                {t('common.continue')}
              </button>
            </div>
          </div>
        ) : null}

        {!incomingRequest && activeSession ? (
          <div className="space-y-3">
            <div>
              <div className="text-sm font-semibold text-[color:var(--text-strong)]">
                {activeSession.direction === 'outgoing' ? t('directFileTransfer.outgoingTitle') : t('directFileTransfer.receivingTitle')}
              </div>
              <div className="mt-1 text-sm text-[color:var(--text-muted)]">
                {activeSession.peerDisplayName}
              </div>
            </div>
            <div className="rounded-[1rem] border border-[color:var(--border-soft)] bg-[color:var(--surface-soft)] p-3 text-sm">
              <div className="font-medium text-[color:var(--text-strong)]">{activeSession.metadata.fileName}</div>
              <div className="mt-1 text-[color:var(--text-muted)]">
                {statusText}
              </div>
            </div>
            {activeSession.error ? (
              <div className="rounded-[0.9rem] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {activeSession.error}
              </div>
            ) : null}
            {activeSession.offlineHint ? (
              <div className="rounded-[0.9rem] border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                {t('directFileTransfer.recipientOfflineHint')}
              </div>
            ) : null}
            <progress
              className="server-transfer-progress"
              max={100}
              value={Math.max(0, Math.min(100, activeSession.progress))}
            />
            <div className="flex items-center gap-2">
              {activeSession.status === 'completed' && activeSession.receivedBlob ? (
                <button
                  type="button"
                  className="btn btn-primary flex-1"
                  onClick={async () => {
                    const blob = activeSession.receivedBlob!;
                    if (await saveBlobWithNativePicker(blob, activeSession.metadata.fileName)) return;
                    const url = URL.createObjectURL(blob);
                    try {
                      const anchor = document.createElement('a');
                      anchor.href = url;
                      anchor.download = activeSession.metadata.fileName;
                      anchor.click();
                    } finally {
                      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
                    }
                  }}
                >
                  {t('messages.directFile.save')}
                </button>
              ) : null}
              {activeSession.canRetry ? (
                <button
                  type="button"
                  className="btn btn-primary flex-1"
                  onClick={() => {
                    void directFileTransferManager.retryOutgoingTransfer(activeSession.sessionId);
                  }}
                >
                  {t('common.retry')}
                </button>
              ) : null}
              <button
                type="button"
                className="btn btn-secondary flex-1"
                onClick={() => {
                  if (isTerminalState) {
                    directFileTransferManager.dismissSession(activeSession.sessionId);
                    return;
                  }
                  directFileTransferManager.endSession(activeSession.sessionId);
                }}
              >
                {isTerminalState ? t('common.close') : t('common.cancel')}
              </button>
            </div>
          </div>
        ) : null}

        {!incomingRequest && !activeSession && waitingRequest ? (
          <div className="space-y-3">
            <div>
              <div className="text-sm font-semibold text-[color:var(--text-strong)]">{t('directFileTransfer.waitingIncomingTitle')}</div>
              <div className="mt-1 text-sm text-[color:var(--text-muted)]">
                {waitingRequest.peerDisplayName}
              </div>
            </div>
            <div className="rounded-[1rem] border border-[color:var(--border-soft)] bg-[color:var(--surface-soft)] p-3 text-sm">
              <div className="font-medium text-[color:var(--text-strong)]">{t('directFileTransfer.readyToReceive')}</div>
              <div className="mt-1 text-[color:var(--text-muted)]">{t('directFileTransfer.waitingForIncoming')}</div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="btn btn-secondary flex-1"
                onClick={() => directFileTransferManager.cancelIncomingWait()}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
